import clsx from 'clsx';
import { IconButton } from '../../Button';
import { ElementsForm } from '../../ElementsForm';
import { MessageExpandableTagListProps } from '../../MessageExpandableTagList/MessageExpandableTagList';
import { useAdvisorCreateMessageForm, useAdvisorCreateMessageFormFields } from '../../MessageForm';
import { ShortcutLegendIcon } from '../../ShortcutLegendIcon';
import { Tooltip, TooltipContainer } from '../../Tooltip';
import { TextareaAutosize } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { listedParticipantNames, participantsForFirmClientDisplayNames } from '../../../../Conversation';
import { Conversation, FirmClient } from '../../../../generated/graphql';
import { CreateFileV2 } from '../../../../File';
import { MessageActionMenu, MessageActionMenuProps } from '../../MessageActionMenu';
import { AdvisorCreateMessageExpandableTagList } from '../../AdvisorCreateMessageExpandableTagList';

export interface CreateMessageFormProps {
  conversation: Conversation;
  isNewConversation: boolean;
  useFormReturn: ReturnType<typeof useAdvisorCreateMessageForm>;
  isCreateMessageLoading: boolean;
  isScrollingMessageThread?: boolean;
  createFileTenantID: string | undefined;
  firmClient: FirmClient | undefined | null;
  messageExpandableTagListProps: Pick<
    MessageExpandableTagListProps,
    'expandedTagListID' | 'setExpandedTagListID' | 'floatingOverrides'
  >;
  messageActionMenuProps: Pick<MessageActionMenuProps, 'routes' | 'clickHandlers' | 'visibilityOptions'>;
}

export function CreateMessageForm({
  firmClient,
  conversation,
  useFormReturn,
  isNewConversation,
  createFileTenantID,
  isCreateMessageLoading,
  messageActionMenuProps,
  isScrollingMessageThread,
  messageExpandableTagListProps,
}: CreateMessageFormProps) {
  const { t: tForm } = useTranslation('form');
  const { t: tCommon } = useTranslation('common');
  const { t: tMessaging } = useTranslation('inAppMessaging');

  const { inputRef, messageField, onFileCreation, focusMessageBox, onRemovePendingFile, tagItems } =
    useAdvisorCreateMessageFormFields({ useFormReturn });

  return (
    <ElementsForm className="flex flex-col" useFormReturn={useFormReturn}>
      <div className="h-full flex items-start flex-col justify-center">
        <TextareaAutosize
          autoFocus
          maxRows={8}
          ref={inputRef}
          name={messageField.name}
          value={messageField.value}
          onBlur={messageField.onBlur}
          onChange={messageField.onChange}
          placeholder={tMessaging('conversation-message-box-placeholder', {
            participantNames: isNewConversation
              ? participantsForFirmClientDisplayNames(firmClient)
              : listedParticipantNames(conversation),
          })}
          className="bg-transparent border-none resize-none w-full text-white text-lg leading-[18px] tracking-[0.286px] placeholder-shown:text-[18px] placeholder-shown:text-lightMode-gray-light/70 placeholder-shown:font-normal"
        />
        <div className="relative w-full">
          <AdvisorCreateMessageExpandableTagList
            clickHandlers={{
              onRemovePendingFile: (file) => onRemovePendingFile(file),
              onRemoveOnboardingReminder: () => useFormReturn.formMethods.setValue('hasOnboardingReminder', false),
            }}
            messageExpandableTagListProps={{
              tagItems,
              verticalPlacement: 'bottom',
              layoutIDPrefix: 'create-message-box',
              disableAnimation: isScrollingMessageThread,
              className: (expanded) => clsx(expanded && 'w-[376px]'),
              ...messageExpandableTagListProps,
            }}
          />
        </div>
      </div>
      <div className="flex mt-6 content-end ml-3">
        <MessageActionMenu {...messageActionMenuProps} />
        {createFileTenantID && (
          <CreateFileV2
            theme="text"
            size="small"
            className="mr-auto"
            iconName="attachment"
            tenantID={createFileTenantID}
            onFileCreation={onFileCreation}
            label={tForm('general-add-attachment')}
          />
        )}
        <Tooltip
          placement="top"
          offset={[0, 8]}
          floatingElement={
            <TooltipContainer>
              {tCommon('send')}
              <ShortcutLegendIcon shortcutCharacter="cmd + return" />
            </TooltipContainer>
          }
        >
          <IconButton
            icon="send"
            theme="primary"
            type="submit"
            className="flex"
            onClick={focusMessageBox}
            isLoading={isCreateMessageLoading}
          />
        </Tooltip>
      </div>
    </ElementsForm>
  );
}
